import * as React from "react";
import * as moment from "moment";
import { observer } from "mobx-react";
import StringsStore from "sh-strings/store";
import { Link, MessageBar, MessageBarActions, Button } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import PastScheduleListItem from "./PastScheduleListItem";
import { TeamDataService, InstrumentationService, ECSConfigService, ECSConfigKey } from "sh-services";
import { UrlFactory } from "sh-application";
import DateTimeFormatter, { DateTimeFormatType } from "sh-application/utility/DateTimeFormatter";
import Spinner, { SpinnerSize } from "sh-application/components/common/Spinner";
import { getDurationInMs } from "sh-application/utility/utility";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import { StaffHubErrorCodes } from "sh-services";
import { trace } from "owa-trace";
import { StaffHubHttpError } from "sh-application/utility/StaffHubError";
import { NewShiftsDialogForDeleteRestoreTeam } from "../../common/NewShiftsDialog/NewShiftsDialogForDeleteRestoreTeam";
import { IRestorableScheduleTeam } from "sh-models";

const styles = require("./PastSchedulesDialog.scss");

export interface PastSchedulesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onRestore: (scheduleId: string) => void;
    teamName: string;
    pastSchedules: IRestorableScheduleTeam[];
}

interface PastSchedulesDialogState {
    isRestoring: boolean;
    errorMessage: string | null;
}

/**
 * Dialog component for displaying and restoring past deleted schedules
 */
@observer
export default class PastSchedulesDialog extends React.Component<PastSchedulesDialogProps, PastSchedulesDialogState> {
    private _strings: Map<string, string>;
    private _firstSelectableItemRef = React.createRef<HTMLButtonElement>();

    constructor(props: PastSchedulesDialogProps) {
        super(props);
        this._strings = StringsStore().registeredStringModules.get("msTeamPicker").strings;
        this.state = {
            isRestoring: false,
            errorMessage: null
        };
    }

    componentDidMount() {
        if (this._firstSelectableItemRef && this._firstSelectableItemRef.current) {
            this._firstSelectableItemRef.current.focus();
        }
    }

    /**
     * Extract error message from error object
     */
    private getErrorMessageFromError(error: StaffHubHttpError): string {
        let errorMessage: string;

        // Check for network errors first
        if (error && error.isNetworkError) {
            errorMessage = this._strings.get("networkError") || "Network error occurred.";
        }else if (error && error.staffHubTopLevelErrorCode) {
            switch (error.staffHubTopLevelErrorCode) {
                case StaffHubErrorCodes.InsufficientPermissions:
                    errorMessage = this._strings.get("insufficientPermissionsError") ||
                        "You don't have permission to restore this schedule.";
                    break;
                case StaffHubErrorCodes.Conflict:
                    errorMessage = this._strings.get("activeScheduleConflictError") ||
                        "Cannot restore while an active schedule exists.";
                    break;
                case StaffHubErrorCodes.NotFound:
                    errorMessage = this._strings.get("scheduleNotFoundError") ||
                        "The schedule you're trying to restore could not be found.";
                    break;
                default:
                    // Use utility for other error codes
                    errorMessage = this._strings.get("genericRestoreError");
            }
        }
        return errorMessage || this._strings.get("genericRestoreError") ||
            "An error occurred while restoring the schedule. Please try again.";
    }

    /**
     * Dismiss error message
     */
    private handleDismissError = () => {
        this.setState({ errorMessage: null });
    }

    /**
     * Calculate days remaining until expiry (configurable days from deletion)
     */
    private calculateDaysUntilExpiry(deletedTime: string): number {
        const deletedDate = moment(deletedTime);
        const expiryDays = ECSConfigService.getECSFeatureSetting(ECSConfigKey.RestoreScheduleExpiryDays) ?? 28;
        const expiryDate = deletedDate.clone().add(expiryDays, "days");
        const today = moment();
        return Math.max(0, expiryDate.diff(today, "days"));
    }

    /**
     * Handle restore button click for a specific schedule
     */
    private handleRestoreSchedule = async (schedule: IRestorableScheduleTeam) => {
        // Clear any previous errors and start restoring
        this.setState({ isRestoring: true, errorMessage: null });

        let restoredScheduleTeamId: string = null;
        let restoreDuration: number = 0;

        try {
            const startTimeStamp: number = InstrumentationService.getCurrentTimeStamp();

            // Fire accessibility alert
            fireAccessibilityAlert(this._strings.get("restoreTeamStartingAriaMessage") || "Restoring schedule...");

            // Call the restore API with the schedule's id and eTag
            await TeamDataService.restoreSchedule(schedule.id, schedule.eTag);
            restoredScheduleTeamId = schedule.id;

            const endTimeStamp: number = InstrumentationService.getCurrentTimeStamp();

            if (restoredScheduleTeamId) {
                restoreDuration = getDurationInMs(startTimeStamp, endTimeStamp);

                // Call the onRestore callback
                this.props.onRestore(schedule.id);
            }
        } catch (error: any) {
            // Type assertion for StaffHubHttpError
            const staffHubError = error as StaffHubHttpError;

            // Extract error message
            const errorMessage = this.getErrorMessageFromError(staffHubError);

            trace.warn("PastSchedulesDialog.RestoreSchedule failed: " + (staffHubError?.message || "Unknown error"));

            // Update state to show error
            this.setState({
                isRestoring: false,
                errorMessage: errorMessage
            });

            // Fire accessibility alert for error
            fireAccessibilityAlert(errorMessage);
        } finally {
            // Log the event
            InstrumentationService.logEvent(
                "PastSchedulesDialog.RestoreSchedule",
                [
                    getGenericEventPropertiesObject(InstrumentationService.properties.EventType, InstrumentationService.values.ScheduleTeamRestored),
                    getGenericEventPropertiesObject(InstrumentationService.properties.RestoreScheduleTeamSuccess, !!restoredScheduleTeamId),
                    getGenericEventPropertiesObject(InstrumentationService.properties.Duration, restoreDuration)
                ]
            );

            // Navigate directly to the restored schedule
            if (restoredScheduleTeamId) {
                window.location.replace(UrlFactory.getSchedulesUrl(restoredScheduleTeamId));
            }
        }
    }

    /**
     * Render loading progress state
     */
    private renderLoadingProgress(): JSX.Element {
        return (
            <div className={styles.progressIndicatorContainer}>
                <Spinner
                    className={styles.spinner}
                    size={SpinnerSize.small}
                    label={this._strings.get("restoreTeamProgressIndicatorMessage") || "Restoring schedule..."}
                />
            </div>
        );
    }

    /**
     * Render the list of past schedules
     */
    private renderScheduleList(): JSX.Element {
        const { pastSchedules } = this.props;

        // Show loading spinner if restoring
        if (this.state.isRestoring) {
            return this.renderLoadingProgress();
        }

        if (!pastSchedules || pastSchedules.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <div>No past schedules available</div>
                </div>
            );
        }

        // Sort by deletedTime in ascending order (earliest deleted first)
        const sortedSchedules = [...pastSchedules].sort((a, b) =>
            moment(a.deletedTime).valueOf() - moment(b.deletedTime).valueOf()
        );

        const scheduleItems = sortedSchedules.map((schedule, index) => {
            const deletedDate = new Date(schedule.deletedTime);
            const deletedDateFormatted = DateTimeFormatter.getDateTimeAsString(deletedDate, DateTimeFormatType.Date_DateMonthYearLong); // e.g., "January 8, 2024"
            const deletedDateShort = DateTimeFormatter.getDateTimeAsString(deletedDate, DateTimeFormatType.Date_Numeric) // e.g., "1/8/2024"
                + " " + DateTimeFormatter.getDateTimeAsString(deletedDate, DateTimeFormatType.Time); // e.g., "2:00 AM"
            const deletionInfo = `Deleted ${deletedDateShort} by ${schedule.deletedByMemberName}`;
            // Calculate days until expiry from deletedTime (28 days from deletion)
            const daysUntilExpiry = this.calculateDaysUntilExpiry(schedule.deletedTime);
            const isFirstItem = index === 0;

            return (
                <PastScheduleListItem
                    key={schedule.id}
                    dateRange={deletedDateFormatted}
                    deletionInfo={deletionInfo}
                    daysUntilExpiry={daysUntilExpiry}
                    onRestore={() => this.handleRestoreSchedule(schedule)}
                    isFirstItem={isFirstItem}
                    buttonRef={isFirstItem ? this._firstSelectableItemRef : undefined}
                />
            );
        });

        return (
            <>
                {this.state.errorMessage && (
                    <MessageBar
                        intent="error"
                        className={styles.errorMessageBar}
                    >
                        <div className={styles.errorMessageContent}>
                            {this.state.errorMessage}
                        </div>
                        <MessageBarActions>
                            <Button
                                appearance="transparent"
                                icon={<DismissRegular />}
                                onClick={this.handleDismissError}
                                aria-label="Dismiss error"
                            />
                        </MessageBarActions>
                    </MessageBar>
                )}
                <div className={styles.scheduleListContainer} role="list">
                    {scheduleItems}
                </div>
            </>
        );
    }

    /**
     * Render the footer with "Not seeing your schedule?" link
     */
    private renderFooter(): JSX.Element {
        const learnWhyUrl = "https://support.microsoft.com/office/get-started-in-shifts-5f3e30d8-1821-4904-be26-c3cd25a497d6";

        return (
            <div className={styles.footer}>
                <div className={styles.footerLeft}>
                    <span>Not seeing your schedule? </span>
                    <Link href={learnWhyUrl} target="_blank" rel="noopener noreferrer">
                        Learn why
                    </Link>
                </div>
                <div className={styles.footerRight}>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={this.props.onClose}
                        disabled={this.state.isRestoring}
                        aria-label={this._strings.get("pastSchedulesBackButtonAriaLabel")}
                    >
                        Back
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { teamName, isOpen, onClose } = this.props;
        const dialogTitle = this._strings.get("pastSchedulesDialogTitle") || "Past schedules";
        const dialogSubtextTemplate = this._strings.get("pastSchedulesDialogSubtext") || "Restore a previously deleted schedule, with all of its data, to the {0} team.";
        const dialogSubtext = dialogSubtextTemplate.replace("{0}", teamName);

        // Prevent closing while restoring
        const handleDismiss = () => {
            if (!this.state.isRestoring) {
                onClose();
            }
        };

        return (
            <NewShiftsDialogForDeleteRestoreTeam
                title={dialogTitle}
                subtext={dialogSubtext}
                content={this.renderScheduleList()}
                actions={this.renderFooter()}
                isOpen={isOpen}
                onDismiss={handleDismiss}
                showDismiss={!this.state.isRestoring}
                modalType="modal"
                ariaLabel={dialogTitle}
                closeOnOutsideClick={false}
                customStyles={{
                    dialogSurface: styles.dialogSurface,
                    dialogBody: styles.dialogBody,
                    dialogContent: styles.dialogContent,
                    dialogActions: styles.dialogActions,
                    dialogSubtext: styles.dialogSubtext,
                    dialogBodyContent: styles.dialogBodyContent
                }}
            />
        );
    }
}
